import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Cards-view drill-in for a project.
 *
 * The visible project is React state, not `searchParams.get('project')`.
 * After a search + move, the address bar is often only `?usecaseId=…` while
 * the UI is still on "<project> (0)". Closing via `location.search` is then a
 * no-op because `project` is already missing from the URL. Setting state to
 * null on Back leaves the view even when the query string never had `project`.
 */
export const useProjectDrillIn = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlProject = searchParams.get('project');
    const [selectedProject, setSelectedProject] = useState(urlProject);
    const locallyClosedRef = useRef(false);

    useEffect(() => {
        if (locallyClosedRef.current) {
            if (!urlProject) {
                locallyClosedRef.current = false;
            }
            return;
        }
        setSelectedProject(urlProject);
    }, [urlProject]);

    const openProject = useCallback((projectName) => {
        locallyClosedRef.current = false;
        setSelectedProject(projectName);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('project', projectName);
            return next;
        });
    }, [setSearchParams]);

    const closeProject = useCallback(() => {
        locallyClosedRef.current = true;
        setSelectedProject(null);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('project');
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const renameDrilledProject = useCallback((nextName) => {
        setSelectedProject(nextName);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('project', nextName);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    return {
        selectedProject,
        openProject,
        closeProject,
        renameDrilledProject,
    };
};
